import React, { useState, useEffect } from 'react';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const UnpostedTransactionsReport = () => {
  const [unpostedTransactions, setUnpostedTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [postingLoading, setPostingLoading] = useState(false);
  const [postingMessage, setPostingMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPeriodInfo, setSelectedPeriodInfo] = useState(null);

  useEffect(() => {
    fetchAccountingPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchUnpostedTransactions(selectedPeriod);
    }
  }, [selectedPeriod]);

  const fetchAccountingPeriods = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/accounting-periods`);
      setAccountingPeriods(response.data);
      // Set default to most recent open period
      const openPeriods = response.data.filter(p => p.status === 'open');
      if (openPeriods.length > 0) {
        const mostRecent = openPeriods.sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        })[0];
        setSelectedPeriod(mostRecent.vuid);
      }
    } catch (error) {
      console.error('Error fetching accounting periods:', error);
      setError('Failed to fetch accounting periods');
    }
  };

  const fetchUnpostedTransactions = async (periodVuid) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${baseURL}/api/unposted-transactions/${periodVuid}`);
      if (response.data.success) {
        setUnpostedTransactions(response.data.unposted_transactions);
        setSelectedPeriodInfo(response.data.accounting_period);
        setSelectedTransactions([]); // Clear selections when fetching new data
      } else {
        setError(response.data.error || 'Failed to fetch unposted transactions');
      }
    } catch (error) {
      console.error('Error fetching unposted transactions:', error);
      setError('Failed to fetch unposted transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTransaction = (transactionVuid) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionVuid) 
        ? prev.filter(id => id !== transactionVuid)
        : [...prev, transactionVuid]
    );
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === filteredTransactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions.map(t => t.transaction_vuid));
    }
  };

  const handlePostSelected = async () => {
    if (selectedTransactions.length === 0) {
      alert('Please select at least one transaction to post');
      return;
    }

    if (!window.confirm(`Are you sure you want to post ${selectedTransactions.length} selected transactions?`)) {
      return;
    }

    setPostingLoading(true);
    setPostingMessage('');
    
    try {
      const response = await axios.post(`${baseURL}/api/unposted-transactions/post-selected`, {
        transaction_vuids: selectedTransactions,
        posted_by: 'User'
      });

      if (response.data.success) {
        setPostingMessage(`Successfully posted ${response.data.posted_count} out of ${response.data.total_selected} transactions`);
        
        // Show any errors
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('Posting errors:', response.data.errors);
          setPostingMessage(prev => prev `\nErrors: ${response.data.errors.join(', ')}`);
        }
        
        // Refresh the data
        fetchUnpostedTransactions(selectedPeriod);
      } else {
        setPostingMessage(`Error: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error posting transactions:', error);
      setPostingMessage(`Error posting transactions: ${error.response?.data?.error || error.message}`);
    } finally {
      setPostingLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle numeric values
      if (sortConfig.key === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return + 0;
    });
  };

  const filteredTransactions = unpostedTransactions.filter(transaction => {
    const matchesType = filterType === 'all' || transaction.transaction_type === filterType;
    const matchesSearch = !searchTerm || 
      transaction.reference_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transaction.project_name && transaction.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (transaction.vendor_name && transaction.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesType && matchesSearch;
  });

  const sortedTransactions = getSortedData(filteredTransactions);

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'ap_invoice': 'AP Invoice',
      'project_billing': 'Project Billing',
      'labor_cost': 'Labor Cost',
      'project_expense': 'Project Expense',
      'multi_project_allocation': 'Multi-Project Allocation'
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'ap_invoice': 'bg-blue-100 text-blue-800',
      'project_billing': 'bg-green-100 text-green-800',
      'labor_cost': 'bg-yellow-100 text-yellow-800',
      'project_expense': 'bg-purple-100 text-purple-800',
      'multi_project_allocation': 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const selectedPeriodData = accountingPeriods.find(p => p.vuid === selectedPeriod);
  const totalAmount = filteredTransactions.reduce((sum, t) => sum + t.total_amount, 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Unposted Transactions Report</h1>
        <p className="text-gray-600">
          Review and post unposted transactions before closing the accounting period
        </p>
      </div>

      {/* Period Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center space-x-4">
          <label htmlFor="period-select" className="text-sm font-medium text-gray-700">
            Accounting Period:
          </label>
          <select
            id="period-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a period...</option>
            {accountingPeriods.map(period => (
              <option key={period.vuid} value={period.vuid}>
                {period.month}/{period.year} ({period.status})
              </option>
            ))}
          </select>
          {selectedPeriodData && (
            <div className="text-sm text-gray-600">
              Status: <span className={`font-medium ${selectedPeriodData.status === 'closed' ? 'text-red-600' : 'text-green-600'}`}>
                {selectedPeriodData.status.toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Closed Period Warning */}
      {selectedPeriodInfo && selectedPeriodInfo.status === 'closed' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75-1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Closed Accounting Period
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This accounting period ({selectedPeriodInfo.month}/{selectedPeriodInfo.year}) is closed. 
                  Transactions cannot be posted to closed periods. Please select an open period to post transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {selectedPeriod && !loading && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{filteredTransactions.length}</div>
              <div className="text-sm text-gray-600">Unposted Transactions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">${totalAmount.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Total Amount</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{selectedTransactions.length}</div>
              <div className="text-sm text-gray-600">Selected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                ${selectedTransactions.reduce((sum, vuid) => {
                  const transaction = unpostedTransactions.find(t => t.transaction_vuid === vuid);
                  return sum + (transaction ? parseFloat(transaction.total_amount || 0) : 0);
                }, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Selected Amount</div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      {selectedPeriod && !loading && unpostedTransactions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="ap_invoice">AP Invoices</option>
                <option value="project_billing">Project Billings</option>
                <option value="labor_cost">Labor Costs</option>
                <option value="project_expense">Project Expenses</option>
                <option value="multi_project_allocation">Multi-Project Allocations</option>
              </select>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSelectAll}
                disabled={selectedPeriodInfo && selectedPeriodInfo.status === 'closed'}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {selectedTransactions.length === filteredTransactions.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handlePostSelected}
                disabled={selectedTransactions.length === 0 || postingLoading || (selectedPeriodInfo && selectedPeriodInfo.status === 'closed')}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {postingLoading ? 'Posting...' : `Post Selected (${selectedTransactions.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Posting Message */}
      {postingMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div className="text-sm text-blue-800 whitespace-pre-line">{postingMessage}</div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-600">Loading unposted transactions...</div>
        </div>
      )}

      {/* Transactions Table */}
      {selectedPeriod && !loading && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {sortedTransactions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-600">
                {unpostedTransactions.length === 0 
                  ? 'No unposted transactions found for this period'
                  : 'No transactions match your current filters'
                }
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedTransactions.length === filteredTransactions.length && filteredTransactions.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('transaction_type')}
                    >
                      Type {sortConfig.key === 'transaction_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('reference_number')}
                    >
                      Reference {sortConfig.key === 'reference_number' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('description')}
                    >
                      Description {sortConfig.key === 'description' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('project_name')}
                    >
                      Project {sortConfig.key === 'project_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('vendor_name')}
                    >
                      Vendor {sortConfig.key === 'vendor_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('total_amount')}
                    >
                      Amount {sortConfig.key === 'total_amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      Created {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTransactions.map((transaction) => (
                    <tr key={transaction.transaction_vuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.includes(transaction.transaction_vuid)}
                          onChange={() => handleSelectTransaction(transaction.transaction_vuid)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeColor(transaction.transaction_type)}`}>
                          {getTransactionTypeLabel(transaction.transaction_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {transaction.reference_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.project_name ? (
                          <div>
                            <div className="font-medium">{transaction.project_name}</div>
                            <div className="text-gray-500">{transaction.project_number}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.vendor_name || <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${transaction.total_amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnpostedTransactionsReport;
